import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { ImageFolder, SubImageFolder } from "../../enum/imageCategoryEnums";
import { deleteFilesInDir, generateImagesArrJson, syncImages } from "../../services/firebase.service";
import { Pagination } from "../../types/Pagination";
import { ServiceRecord } from "../../types/ServiceRecord";
import { resetCacheStamp } from "../../util/cacheManager";
import { generateFilterString, generatePaginationString, handleNetworkResponse } from "../../util/helper";
import { config } from "../../config";
import { RecordsFilterType } from "../../types/filters/RecordsFilterType";
import { CacheStoreKey } from "../../enum/cacheStore.enum";
import { Persister } from "../../util/persister";
import { PopupManager } from "../../util/popupManager";

// Define a service using a base URL and expected endpoints
export const recordApi = createApi({
  reducerPath: "recordApi",
  tagTypes: [CacheStoreKey.userCacheStampRecords],
  baseQuery: fetchBaseQuery({ baseUrl: "" }),
  endpoints: (build) => ({

    // mutation
    createRecord: build.mutation({
      queryFn: async (args: { rec: ServiceRecord; imgs: Record<number, Blob | null> }) => {
        resetCacheStamp(CacheStoreKey.userCacheStampRecords);
        // @ts-expect-error
        delete args.rec.id;
        args.rec.images = generateImagesArrJson(args.rec.UserId, args.rec.images, args.imgs, ImageFolder.Vehicle, args.rec.VehicleId, [SubImageFolder.Records, config.tempResourceId]);
        const response = await axios.post(
          `/record/create`,
          args.rec
        );
        if (response.data.status && args.imgs) {
          await syncImages(args.rec.UserId, ImageFolder.Vehicle, args.rec.VehicleId, args.imgs, [SubImageFolder.Records, response.data.data.id]);
        }
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.userCacheStampRecords],
    }),

    // mutation
    updateRecord: build.mutation({
      queryFn: async (args: { rec: ServiceRecord; imgs: Record<number, Blob | null> }) => {

        resetCacheStamp(CacheStoreKey.userCacheStampRecords);

        args.rec.images = generateImagesArrJson(args.rec.UserId, args.rec.images, args.imgs, ImageFolder.Vehicle, args.rec.VehicleId, [SubImageFolder.Records, args.rec.id]);
        const response = await axios.post(
          `/record/update`,
          args.rec
        );
        if (response.data.status && args.imgs) {
          await syncImages(args.rec.UserId, ImageFolder.Vehicle, args.rec.VehicleId, args.imgs, [SubImageFolder.Records, response.data.data.id]);
        }
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.userCacheStampRecords],
    }),

    // mutation
    deleteRecord: build.mutation({
      queryFn: async (record: ServiceRecord) => {
        resetCacheStamp(CacheStoreKey.userCacheStampRecords);
        PopupManager.showInfoToast('Deleting images...');
        await deleteFilesInDir(record.UserId, ImageFolder.Vehicle, record.VehicleId, [SubImageFolder.Records, record.id]);
        PopupManager.showInfoToast('Deleting spare part...');
        const response = await axios.post(`/record/delete/${record.id}`);
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.userCacheStampRecords],
    }),

    // read
    readRecord: build.query({
      queryFn: async (args: { id: string }) => {
        if (args.id === "-1") {
          return {
            data: {},
          };
        }
        // const response = await axios.get(setCacheStamp(`/record/${args.id}`, CacheStoreKey.recordDBStamp));
        const response = await Persister.requestData(CacheStoreKey.noCache, 'GET', `/record/${args.id}`);
        return handleNetworkResponse(response, null);
      },
      providesTags: [CacheStoreKey.userCacheStampRecords],
    }),

    // read
    searchRecords: build.query({
      queryFn: async (args: Pagination & { filter: Partial<RecordsFilterType>}) => {
        const filterString = generateFilterString(args.filter);
        const paginationString = generatePaginationString(args.limit, args.offset);

        const response = await Persister.requestData(CacheStoreKey.noCache, 'GET', `/record/search?${filterString}${paginationString}`);
        return handleNetworkResponse(response, []);
      },
      providesTags: [CacheStoreKey.userCacheStampRecords],
    })

  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useCreateRecordMutation,
  useUpdateRecordMutation,
  useDeleteRecordMutation,
  useReadRecordQuery,
  useSearchRecordsQuery,
  useLazySearchRecordsQuery,
} = recordApi;

export type RecordApiType = typeof recordApi;